import React from 'react';
import { connect } from 'react-redux';
import { Field, change, formValueSelector, reduxForm } from 'redux-form';
import { string, number, PropTypes } from 'prop-types';
import { Button, FormGroup, Form } from 'reactstrap';
import {
  isRequired,
  validateIntegerNumber,
  validateMaxLength,
} from '../../../../utils/validators';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import FormFooter from '../../../common/forms/FormFooter';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import MultiPaymentMethodInputGroup, {
  hasPendingPinpadSales,
  isPinpadLocked,
} from '../../../common/forms/MultiPaymentMethodInputGroup';
import {
  PARCEL_STATUS,
  PAYMENT_METHOD_PAGO_EN_DESTINO,
} from '../../../../config/constants';
import SelectionableTable from '../../../common/forms/table/SelectionableTable';
import Select from '../../../common/forms/select/Select';

const FORM_NAME = 'CargoPickupModelLegalAbandonmentForm';

const validateMaxLength10 = validateMaxLength(10);

const selector = formValueSelector(FORM_NAME);

const getDisplayPriceFromParcel = (parcel) => {
  if (!parcel) {
    return '';
  }

  if (
    parcel.salePriceWithDiscount !== null &&
    parcel.salePriceWithDiscount !== undefined
  ) {
    return parcel.salePriceWithDiscount.toFixed(2);
  }

  if (
    parcel.salePriceWithDetraction !== null &&
    parcel.salePriceWithDetraction !== undefined
  ) {
    return parcel.salePriceWithDetraction.toFixed(2);
  }

  if (parcel.salePrice !== null && parcel.salePrice !== undefined) {
    return parcel.salePrice.toFixed(2);
  }

  return '';
};

const CargoPickupModalLegalAbandonment = ({
  handleSubmit,
  parcel,
  handleProcess,
  onCancel,
  options,
  totalPrice,
  pinpadLocked,
  pendingSalesQueued,
  onPinpadTransactionApproved,
  pendingPinpadSales,
  dispatchChange,
}) => {
  // the amount to pay follows the restored charge
  const onPendingSaleRestored = (sale) =>
    dispatchChange(FORM_NAME, 'amountToPay', sale.amount.toFixed(2));

  const data = parcel.cargoItemList.filter(
    (item) =>
      item.status !== PARCEL_STATUS.DELIVERED.value &&
      item.status !== PARCEL_STATUS.LOST.value &&
      item.status !== PARCEL_STATUS.UNCLAIMED_LOST.value,
  );

  const generateBaggageItemColumns = [
    {
      Header: 'Descripción',
      accessor: 'description',
      className: 'text-center',
    },
    {
      Header: 'Categoria',
      accessor: 'cargoCategoryName',
      className: 'text-center',
    },
    {
      Header: 'Peso (kg)',
      accessor: 'weight',
      className: 'text-center',
    },
    {
      Header: 'Correlativo',
      accessor: 'correlation',
      className: 'text-center',
    },
  ];

  return (
    <Form onSubmit={handleSubmit(handleProcess)}>
      <ResourceProperty label="Serie:">
        {parcel.documentSeries}
      </ResourceProperty>

      <ResourceProperty label="Número de serie:">
        {parcel.documentCode}
      </ResourceProperty>

      <ResourceProperty label="Origen:">
        {parcel.sourceCityName}
      </ResourceProperty>

      <ResourceProperty label="Destino:">
        {parcel.destinationCityName}
      </ResourceProperty>

      <hr />

      <SelectionableTable
        columns={generateBaggageItemColumns}
        data={data}
        keyField="id"
        required
        filterable
        form={FORM_NAME}
        returnOnlySelectedItems
      />

      <FormGroup row className="mt-4">
        <FormItem label="Monto a Pagar" required>
          <Field
            name="amountToPay"
            component={TextInput}
            type="text"
            placeholder="Monto a pagar"
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row className="mt-4">
        <FormItem label="Clave" required>
          <Field
            name="secretCode"
            component={TextInput}
            type="text"
            placeholder="Clave secreta"
            validate={[isRequired, validateIntegerNumber, validateMaxLength10]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row className="mt-4">
        <FormItem label="Se entrega a" required>
          <Field
            name="deliveredCustomer"
            component={Select}
            type="text"
            placeholder="Seleccione al Consignado a Entregar"
            validate={[isRequired]}
            options={options}
          />
        </FormItem>
      </FormGroup>

      <MultiPaymentMethodInputGroup
        formName={FORM_NAME}
        totalPrice={totalPrice}
        maxPaymentMethods={1}
        transactionCodesNotIncluded={[PAYMENT_METHOD_PAGO_EN_DESTINO]}
        onPinpadTransactionApproved={onPinpadTransactionApproved}
        pendingPinpadSales={pendingPinpadSales}
        onPendingSaleRestored={onPendingSaleRestored}
      />

      <FormFooter
        saveButtonColor="secondary"
        saveButtonIcon="fa fa-share"
        saveButtonText="Entregar"
        saveButtonDisabled={pinpadLocked || pendingSalesQueued}
      >
        <Button color="primary" onClick={onCancel}>
          Cancelar
        </Button>
      </FormFooter>
    </Form>
  );
};

const parcelPropTypes = {
  documentSeries: string,
  documentCode: number,
  sourceCityName: string,
  destinationCityName: string,
  paymentMethodId: number,
  salePriceWithDiscount: number,
  salePriceWithDetraction: number,
  salePrice: number,
  cargoItemList: PropTypes.arrayOf(PropTypes.shape({})),
};

CargoPickupModalLegalAbandonment.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  parcel: PropTypes.shape(parcelPropTypes).isRequired,
  handleProcess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  totalPrice: PropTypes.number.isRequired,
  pinpadLocked: PropTypes.bool.isRequired,
  pendingSalesQueued: PropTypes.bool.isRequired,
  onPinpadTransactionApproved: PropTypes.func.isRequired,
  pendingPinpadSales: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      isQrPayment: PropTypes.bool,
      transaction: PropTypes.shape({
        id: PropTypes.number,
        financialReference: PropTypes.string,
      }),
    }),
  ),
  dispatchChange: PropTypes.func.isRequired,
};

CargoPickupModalLegalAbandonment.defaultProps = {
  pendingPinpadSales: [],
};

const mapStateToProps = (state, ownProps) => {
  // an already charged pinpad sale fixes the amount to what was charged
  const [firstPendingSale] = ownProps.pendingPinpadSales || [];
  const displayPrice = firstPendingSale
    ? firstPendingSale.amount.toFixed(2)
    : getDisplayPriceFromParcel(ownProps.parcel);
  const amountToPay = selector(state, 'amountToPay');

  return {
    initialValues: {
      amountToPay: displayPrice,
    },
    totalPrice:
      parseFloat(amountToPay !== undefined ? amountToPay : displayPrice) || 0,
    pinpadLocked: isPinpadLocked(FORM_NAME)(state),
    pendingSalesQueued: hasPendingPinpadSales(FORM_NAME)(state),
  };
};

const mapDispatchToProps = {
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
})(CargoPickupModalLegalAbandonment);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
